/**
 * User-controllable UI settings — theme, density, accent.
 *
 * Single source of truth, persisted to localStorage so a return visit
 * looks the same. Reads `data-theme` / `data-density` / `data-accent`
 * attributes off `<html>` and writes them back on every change so the
 * CSS layer in tokens.css picks up the swap without a re-render.
 *
 * Boot order matters: this module is loaded in main.ts BEFORE the
 * Svelte mount so the FOUC window is the smallest possible (a single
 * synchronous attribute write before the first paint).
 */

const STORAGE_KEY = 'cryptarch:settings:v1';

export type Theme = 'dark' | 'light' | 'auto';
export type Density = 'compact' | 'cozy' | 'comfortable';
export type Accent = 'solar' | 'arc' | 'void' | 'stasis';

export const THEMES: Theme[] = ['dark', 'light', 'auto'];
export const DENSITIES: Density[] = ['compact', 'cozy', 'comfortable'];
export const ACCENTS: Accent[] = ['solar', 'arc', 'void', 'stasis'];

interface Stored {
  theme: Theme;
  density: Density;
  accent: Accent;
}

const DEFAULTS: Stored = {
  theme: 'dark',
  density: 'cozy',
  accent: 'solar',
};

function loadFromStorage(): Stored {
  if (typeof localStorage === 'undefined') return DEFAULTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Stored>;
    return {
      theme: THEMES.includes(parsed.theme as Theme) ? (parsed.theme as Theme) : DEFAULTS.theme,
      density: DENSITIES.includes(parsed.density as Density)
        ? (parsed.density as Density)
        : DEFAULTS.density,
      accent: ACCENTS.includes(parsed.accent as Accent)
        ? (parsed.accent as Accent)
        : DEFAULTS.accent,
    };
  } catch {
    return DEFAULTS;
  }
}

const initial = loadFromStorage();
let _theme: Theme = $state(initial.theme);
let _density: Density = $state(initial.density);
let _accent: Accent = $state(initial.accent);

export const settings = {
  get theme(): Theme {
    return _theme;
  },
  get density(): Density {
    return _density;
  },
  get accent(): Accent {
    return _accent;
  },
};

/** Map our 3-value density choice into the 2-value data-density that
 *  tokens.css ships with. Comfortable is 'cozy' (the default scale of 1);
 *  cozy and compact map directly. */
function densityAttr(d: Density): string | null {
  if (d === 'compact') return 'compact';
  if (d === 'comfortable') return 'cozy';
  return null; // cozy = default = no attribute
}

function persist(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ theme: _theme, density: _density, accent: _accent }),
    );
  } catch {
    // localStorage can be disabled / full; we silently fall back to in-memory.
  }
}

/** Apply the current settings to <html>. Idempotent — safe to call in
 *  $effect blocks. */
export function applySettings(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = _theme;
  const dAttr = densityAttr(_density);
  if (dAttr) root.dataset.density = dAttr;
  else delete root.dataset.density;
  if (_accent === 'solar') delete root.dataset.accent;
  else root.dataset.accent = _accent;
}

export function setTheme(t: Theme): void {
  _theme = t;
  applySettings();
  persist();
}

export function setDensity(d: Density): void {
  _density = d;
  applySettings();
  persist();
}

export function setAccent(a: Accent): void {
  _accent = a;
  applySettings();
  persist();
}

/** Synchronously apply settings during boot so the first paint already
 *  matches the user's last-saved preferences. Called from main.ts before
 *  Svelte mounts. */
export function bootSettings(): void {
  applySettings();
}
